import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as CertificatesIcon,
  Handyman as ProvidersIcon,
  FactCheck as ApprovalsIcon,
  Psychology as AIFlowIcon,
  AutoAwesome as AskAIIcon,
  AccountBalance as AccountingIcon,
  Apartment as PropertiesIcon,
  PermIdentity as PeopleIcon,
  Person,
  LogoutOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MillosLogo from './MillosLogo';

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  pendingRenewalsCount?: number;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  hasUpdates?: boolean;
  // Logout doesn't navigate to a route — it calls this instead, when set.
  onClick?: () => void;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isExpanded, onToggle, pendingRenewalsCount = 0 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const showExpanded = isExpanded && window.innerWidth >= 900;

  const menuGroups: MenuGroup[] = [
    {
      label: 'COMPLIANCE',
      items: [
        { id: 'dashboard', label: 'Certificates', icon: <DashboardIcon fontSize="small" /> },
        { id: 'providers', label: 'Providers', icon: <ProvidersIcon fontSize="small" /> },
        { id: 'approvals', label: 'Approvals', icon: <ApprovalsIcon fontSize="small" />, hasUpdates: pendingRenewalsCount > 0 },
        { id: 'recentactivity', label: 'Recent Activity', icon: <CertificatesIcon fontSize="small" /> },
      ],
    },
    {
      label: 'MANAGEMENT',
      items: [
        { id: 'properties', label: 'Properties', icon: <PropertiesIcon fontSize="small" /> },
        { id: 'accounting', label: 'Accounting', icon: <AccountingIcon fontSize="small" /> },
        { id: 'people', label: 'People', icon: <PeopleIcon fontSize="small" /> },
      ],
    },
    {
      // POC — see llm-agent-implementation-plan.md. Its own group since it queries across both
      // COMPLIANCE and MANAGEMENT data, rather than belonging under either one specifically.
      label: 'ASSISTANT',
      items: [
        { id: 'copilot', label: 'AI agent', icon: <MillosLogo size={21} agent /> },
        { id: 'ask-ai', label: 'Ask AI', icon: <AskAIIcon fontSize="small" /> },
        { id: 'ai-flow', label: 'How it works', icon: <AIFlowIcon fontSize="small" /> },
      ],
    },
    {
      label: 'USER SETTINGS',
      items: [
        // Profile/Logout used to live in a separate fixed-at-bottom Box that was hidden on
        // mobile (`display: { xs: 'none', md: 'block' }`) — moved into this regular list so
        // they render through the same List/ListItemButton loop, which is visible on mobile.
        { id: 'profile', label: user?.name || 'User', icon: <Person fontSize="small" /> },
        { id: 'logout', label: 'Logout', icon: <LogoutOutlined fontSize="small" />, onClick: logout },
      ],
    },
  ];

  const isActive = (id: string) => location.pathname === `/${id}`;

  return (
    <Box
      sx={{
        width: { xs: 56, md: isExpanded ? 240 : 64 },
        height: '100vh',
        bgcolor: theme.palette.tokens.white,
        borderRight: `1px solid ${theme.palette.tokens.hairline}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* Branding / toggle */}
      <Box
        onClick={onToggle}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          px: showExpanded ? 2.5 : 0,
          pt: 3, // aligns the wordmark's top edge with the page content's own top padding (p: 3)
          pb: 2.5,
          justifyContent: showExpanded ? 'flex-start' : 'center',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
          '&:hover': { bgcolor: theme.palette.tokens.canvas },
        }}
      >
        {showExpanded ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MillosLogo size={28} wordmark />
          </Box>
        ) : (
          <MillosLogo size={28} />
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', pt: 2.5, pb: 1.5 }}>
        {menuGroups.map((group, gi) => (
          <Box key={group.label} sx={{ mb: 0.5 }}>
            {/* Section label — only when expanded */}
            {showExpanded && (
              <Typography sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: theme.palette.tokens.mutedSoft,
                px: 2.5,
                pt: gi === 0 ? 0.5 : 2,
                pb: 0.75,
                display: 'block',
              }}>
                {group.label}
              </Typography>
            )}
            {!showExpanded && gi > 0 && (
              <Box sx={{ mx: 1.5, my: 1, borderTop: `1px solid ${theme.palette.tokens.surfaceSoft}` }} />
            )}

            <List sx={{ p: 0, px: { xs: 0.5, md: 1 } }}>
              {group.items.map((item) => {
                const active = isActive(item.id);
                return (
                  <Tooltip
                    key={item.id}
                    title={showExpanded ? '' : item.label}
                    placement="right"
                  >
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        selected={active}
                        onClick={() => (item.onClick ? item.onClick() : navigate(`/${item.id}`))}
                        sx={{
                          borderRadius: 1.5,
                          minHeight: 40,
                          justifyContent: showExpanded ? 'flex-start' : 'center',
                          px: showExpanded ? 1.5 : 1,
                          position: 'relative',
                          ...(item.hasUpdates && !active && {
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                              '0%': { boxShadow: '0 0 0 0 rgba(25,118,210,0.3)', backgroundColor: 'rgba(25,118,210,0.06)' },
                              '70%': { boxShadow: '0 0 0 5px rgba(25,118,210,0)', backgroundColor: 'rgba(25,118,210,0.12)' },
                              '100%': { boxShadow: '0 0 0 0 rgba(25,118,210,0)', backgroundColor: 'rgba(25,118,210,0.06)' },
                            },
                          }),
                          '&.Mui-selected': {
                            bgcolor: '#eff6ff',
                            '&:hover': { bgcolor: '#eff6ff' },
                          },
                          '&:hover': { bgcolor: theme.palette.tokens.canvas },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: showExpanded ? 1.5 : 0,
                            color: active ? theme.palette.tokens.primary : item.hasUpdates ? theme.palette.tokens.primary : theme.palette.tokens.mutedSoft,
                            justifyContent: 'center',
                          }}
                        >
                          {item.hasUpdates ? (
                            <Box sx={{ position: 'relative', display: 'flex' }}>
                              {item.icon}
                              <Box sx={{
                                position: 'absolute', top: -3, right: -3,
                                width: 7, height: 7,
                                bgcolor: theme.palette.tokens.error, borderRadius: '50%',
                                border: '1.5px solid white',
                              }} />
                            </Box>
                          ) : item.icon}
                        </ListItemIcon>

                        {showExpanded && (
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: active ? 600 : 500,
                                  color: active ? theme.palette.tokens.primary : item.hasUpdates ? theme.palette.tokens.primary : theme.palette.tokens.body,
                                }}>
                                  {item.label}
                                </Typography>
                                {item.hasUpdates && pendingRenewalsCount > 0 && (
                                  <Box sx={{
                                    bgcolor: theme.palette.tokens.error, color: theme.palette.tokens.white,
                                    borderRadius: 10, px: 0.75, py: 0.1,
                                    fontSize: '0.65rem', fontWeight: 700,
                                    ml: 1, minWidth: 18, textAlign: 'center',
                                  }}>
                                    {pendingRenewalsCount}
                                  </Box>
                                )}
                              </Box>
                            }
                            sx={{ m: 0 }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

    </Box>
  );
};

export default Sidebar;
